import styled from 'styled-components';
import { Card, Button, Input } from '../../../styles/components';
import { glassDeep, glassMedium, glassHover } from '../../../styles/components/common/glassmorphism';
import { scaleInAnimation, fadeInAnimation } from '../../../styles/components/common/animations';
import { useState, useEffect } from 'react';

const HeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: -1rem;
    height: 1px;
    background: linear-gradient(
      to right,
      rgba(99, 102, 241, 0.05),
      rgba(99, 102, 241, 0.3),
      rgba(99, 102, 241, 0.05)
    );
  }
`;

const PageTitle = styled.h1`
  font-size: 1.75rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.95);
  margin: 0;
  background: linear-gradient(
    to right,
    rgba(255, 255, 255, 0.9),
    rgba(99, 102, 241, 0.9)
  );
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: ${fadeInAnimation} 0.5s ease-out;
`;

const CreateButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: linear-gradient(
    135deg, 
    rgba(79, 70, 229, 0.7) 0%, 
    rgba(99, 102, 241, 0.7) 100%
  );
  border: 1px solid rgba(99, 102, 241, 0.3);
  padding: 0.7rem 1.2rem;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  font-weight: 600;
  transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  
  &:hover {
    transform: translateY(-3px) scale(1.02);
    background: linear-gradient(
      135deg, 
      rgba(79, 70, 229, 0.8) 0%, 
      rgba(99, 102, 241, 0.8) 100%
    );
    box-shadow: 0 8px 30px rgba(99, 102, 241, 0.3);
  }
  
  &:active {
    transform: translateY(-1px) scale(0.99);
  }
`;

const PlusIcon = styled.span`
  font-size: 1.2rem;
  font-weight: 500;
  line-height: 1;
`;

const WorkshopsContainer = styled(Card)`
  ${glassDeep}
  padding: 1.5rem;
  margin-bottom: 1rem;
  animation: ${fadeInAnimation} 0.4s ease-out;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  position: relative;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      120deg,
      transparent, 
      transparent,
      rgba(99, 102, 241, 0.05),
      transparent,
      transparent
    );
    background-size: 200% 100%;
    opacity: 0;
    transition: opacity 0.3s ease;
    pointer-events: none;
    border-radius: ${({ theme }) => theme.borderRadius.lg};
  }
  
  &:hover:before {
    opacity: 1;
  }
`;

const WorkshopsTable = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  margin-top: 1rem;
  animation: ${scaleInAnimation} 0.3s ease-out;
  overflow: hidden;
  
  thead {
    position: sticky;
    top: 0;
    z-index: 1;
  }
`;

const TableHeader = styled.th`
  text-align: left;
  padding: 0.75rem 1rem;
  ${glassMedium}
  border-bottom: 1px solid rgba(99, 102, 241, 0.3);
  color: rgba(255, 255, 255, 0.9);
  font-weight: 600;
  
  &:first-child {
    border-top-left-radius: ${({ theme }) => theme.borderRadius.md};
  }
  
  &:last-child {
    border-top-right-radius: ${({ theme }) => theme.borderRadius.md};
  }
`;

const TableRow = styled.tr`
  transition: all 0.3s ease;
  position: relative;
  
  &:nth-child(even) {
    background-color: rgba(15, 23, 42, 0.3);
  }
  
  &:hover {
    background-color: rgba(99, 102, 241, 0.1);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    z-index: 1;
  }
  
  &:active {
    transform: translateY(-1px);
  }
`;

const TableCell = styled.td`
  padding: 0.75rem 1rem;
  border-bottom: 1px solid rgba(99, 102, 241, 0.15);
  color: rgba(255, 255, 255, 0.8);
  transition: all 0.3s ease;
  
  ${TableRow}:hover & {
    color: rgba(255, 255, 255, 1);
  }
`;

const ActionCell = styled(TableCell)`
  text-align: right;
  white-space: nowrap;
  opacity: 0.6;
  
  ${TableRow}:hover & {
    opacity: 1;
  }
`;

const TitleCell = styled(TableCell)`
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
  cursor: pointer;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: 0.5rem;
    left: 1rem;
    width: 0;
    height: 1px;
    background: rgba(99, 102, 241, 0.8);
    transition: width 0.3s ease;
  }
  
  ${TableRow}:hover &:after {
    width: calc(100% - 2rem);
  }
`;

const FilterContainer = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
  animation: ${scaleInAnimation} 0.3s ease-out;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.5rem;
  }
`;

const SearchInput = styled(Input)`
  flex: 1;
  min-width: 250px;
  ${glassMedium}
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: 0.75rem 1rem;
  
  &:focus {
    box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.3);
  }
`;

const StatusFilter = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  align-items: center;
`;

const StatusButton = styled.button`
  padding: 0.4rem 0.8rem;
  border-radius: ${({ theme }) => theme.borderRadius.pill};
  border: 1px solid ${({ theme, active }) => active ? 'rgba(99, 102, 241, 0.6)' : 'rgba(255, 255, 255, 0.1)'};
  ${glassMedium}
  background-color: ${({ active }) => active ? 'rgba(99, 102, 241, 0.25)' : 'transparent'};
  color: ${({ active }) => active ? 'rgba(255, 255, 255, 0.95)' : 'rgba(255, 255, 255, 0.6)'};
  cursor: pointer;
  font-size: 0.85rem;
  transition: all 0.3s ease;
  backdrop-filter: blur(${({ active }) => active ? '12px' : '5px'});
  
  &:hover {
    transform: translateY(-2px);
    background-color: ${({ active }) => active ? 'rgba(99, 102, 241, 0.35)' : 'rgba(255, 255, 255, 0.05)'};
    border-color: rgba(99, 102, 241, 0.5);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
  
  &:active {
    transform: translateY(-1px);
  }
`;

const NoWorkshopsMessage = styled.div`
  padding: 3rem 2rem;
  text-align: center;
  color: rgba(255, 255, 255, 0.5);
  ${glassMedium}
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  animation: ${fadeInAnimation} 0.4s ease-out;
  
  &:before {
    content: '📝';
    display: block;
    font-size: 2.5rem;
    margin-bottom: 1rem;
    opacity: 0.7;
  }
`;

const ActionButton = styled(Button)`
  margin-left: 0.5rem;
  padding: 0.4rem 0.8rem;
  font-size: 0.85rem;
  opacity: 0.8;
  transform: scale(0.95);
  border-radius: ${({ theme }) => theme.borderRadius.pill};
  transition: all 0.3s ease;
  
  &:hover {
    opacity: 1;
    transform: scale(1) translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }
  
  &:active {
    transform: scale(0.98) translateY(-1px);
  }
`;

const ActionButtonsWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  transition: all 0.3s ease;
  
  ${TableRow}:hover & {
    transform: scale(1.05);
  }
`;

const ParticipantCount = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.75rem;
  border-radius: ${({ theme }) => theme.borderRadius.pill};
  background: rgba(15, 23, 42, 0.3);
  border: 1px solid rgba(99, 102, 241, 0.15);
  transition: all 0.3s ease;
  
  ${TableRow}:hover & {
    background: rgba(15, 23, 42, 0.5);
    border-color: rgba(99, 102, 241, 0.3);
    transform: scale(1.05);
  }
`;

const CurrentCount = styled.span`
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
`;

const Divider = styled.span`
  color: rgba(255, 255, 255, 0.5);
  font-weight: 300;
`;

const MaxCount = styled.span`
  color: rgba(255, 255, 255, 0.7);
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.3rem 0.75rem;
  border-radius: ${({ theme }) => theme.borderRadius.pill};
  font-size: 0.8rem;
  font-weight: 500;
  letter-spacing: 0.01em;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  
  /* Glass effect based on status */
  background: ${({ status }) => 
    status === 'upcoming' 
      ? 'rgba(79, 70, 229, 0.15)' 
      : status === 'in-progress' 
        ? 'rgba(245, 158, 11, 0.15)' 
        : status === 'completed' 
          ? 'rgba(16, 185, 129, 0.15)' 
          : 'rgba(239, 68, 68, 0.15)'};
          
  /* Border color by status */
  border: 1px solid ${({ status }) => 
    status === 'upcoming' 
      ? 'rgba(79, 70, 229, 0.3)' 
      : status === 'in-progress' 
        ? 'rgba(245, 158, 11, 0.3)' 
        : status === 'completed' 
          ? 'rgba(16, 185, 129, 0.3)' 
          : 'rgba(239, 68, 68, 0.3)'};
          
  /* Text color by status */
  color: ${({ status }) => 
    status === 'upcoming' 
      ? 'rgba(79, 70, 229, 0.9)' 
      : status === 'in-progress' 
        ? 'rgba(245, 158, 11, 0.9)' 
        : status === 'completed' 
          ? 'rgba(16, 185, 129, 0.9)' 
          : 'rgba(239, 68, 68, 0.9)'};
          
  backdrop-filter: blur(5px);
  -webkit-backdrop-filter: blur(5px);
  
  /* Add subtle shimmer effect */
  &:after {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: linear-gradient(
      to right,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 0.1) 50%,
      rgba(255, 255, 255, 0) 100%
    );
    transform: rotate(30deg);
    transition: all 0.7s ease;
    opacity: 0;
  }
  
  ${TableRow}:hover &:after {
    opacity: 1;
    transform: rotate(30deg) translate(100%, -100%);
  }
`;

const DateTimeDisplay = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
`;

const DatePart = styled.div`
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
`;

const TimePart = styled.div`
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.6);
`;

const LocationDisplay = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const LocationIcon = styled.span`
  font-size: 0.9rem;
  opacity: 0.7;
`;

const LocationText = styled.span`
  color: rgba(255, 255, 255, 0.8);
  transition: all 0.3s ease;
  
  ${TableRow}:hover & {
    color: rgba(255, 255, 255, 1);
  }
`;

// Modal components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(15, 23, 42, 0.7);
  backdrop-filter: blur(5px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100; /* Consistent z-index with other modals */
  opacity: ${({ isOpen }) => (isOpen ? 1 : 0)};
  visibility: ${({ isOpen }) => (isOpen ? 'visible' : 'hidden')};
  transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  pointer-events: ${({ isOpen }) => (isOpen ? 'all' : 'none')};
  animation: ${fadeInAnimation} 0.3s ease-out;
`;

const ModalContent = styled.div`
  ${glassDeep}
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: 2rem;
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  transform: ${({ isOpen }) => (isOpen ? 'scale(1) translateY(0)' : 'scale(0.9) translateY(-20px)')};
  opacity: ${({ isOpen }) => (isOpen ? 1 : 0)};
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  position: relative;
  display: flex;
  flex-direction: column;
  animation: ${scaleInAnimation} 0.4s ease-out;
  z-index: 10000; /* Higher than the overlay */
  
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(15, 23, 42, 0.2);
    border-radius: 10px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(99, 102, 241, 0.3);
    border-radius: 10px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: rgba(99, 102, 241, 0.5);
  }
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      135deg,
      transparent,
      rgba(99, 102, 241, 0.05),
      transparent
    );
    border-radius: ${({ theme }) => theme.borderRadius.lg};
    pointer-events: none;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid rgba(99, 102, 241, 0.2);
  position: sticky;
  top: 0;
  width: 100%;
  background: rgba(15, 23, 42, 0.85); /* Semi-transparent background */
  backdrop-filter: blur(10px); /* Additional blur effect */
  padding-top: 0.5rem;
  z-index: 1; /* Ensure it's above the form content */
  
  &:after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 1px;
    background: linear-gradient(
      to right,
      rgba(99, 102, 241, 0.05),
      rgba(99, 102, 241, 0.3),
      rgba(99, 102, 241, 0.05)
    );
  }
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 1.5rem;
  font-weight: 600;
  background: linear-gradient(
    to right,
    rgba(255, 255, 255, 0.9),
    rgba(99, 102, 241, 0.9)
  );
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: ${fadeInAnimation} 0.5s ease-out;
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CloseButton = styled.button`
  background: rgba(15, 23, 42, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: rgba(255, 255, 255, 0.95);
  font-size: 1.5rem;
  cursor: pointer;
  transition: all 0.2s ease;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  z-index: 2; /* Ensure it's clickable */
  position: relative; /* Ensure position context for z-index */
  
  &:hover {
    color: rgba(255, 255, 255, 1);
    background-color: rgba(99, 102, 241, 0.3);
    transform: scale(1.1);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  }
  
  &:active {
    transform: scale(0.95);
  }
`;

const ModalFooter = styled.div`
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid rgba(99, 102, 241, 0.2);
  position: sticky;
  bottom: 0;
  background: rgba(15, 23, 42, 0.85);
  backdrop-filter: blur(10px);
  padding-bottom: 0.5rem;
  z-index: 1;
  
  &:before {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    height: 1px;
    background: linear-gradient(
      to right,
      rgba(99, 102, 241, 0.05),
      rgba(99, 102, 241, 0.3),
      rgba(99, 102, 241, 0.05)
    );
  }
`;

const WorkshopForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  width: 100%;
  padding-top: 0.5rem;
  position: relative;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  animation: ${fadeInAnimation} 0.3s ease-out;
  animation-fill-mode: both;
  
  &:nth-child(1) { animation-delay: 0.05s; }
  &:nth-child(2) { animation-delay: 0.1s; }
  &:nth-child(3) { animation-delay: 0.15s; }
  &:nth-child(4) { animation-delay: 0.2s; }
  &:nth-child(5) { animation-delay: 0.25s; }
  &:nth-child(6) { animation-delay: 0.3s; }
`;

const FormLabel = styled.label`
  font-size: 0.9rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
  margin-left: 0.25rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  &:before {
    content: '';
    display: inline-block;
    width: 3px;
    height: 3px;
    border-radius: 50%;
    background-color: rgba(99, 102, 241, 0.8);
  }
`;

const FormInput = styled.input`
  ${glassMedium}
  padding: 0.75rem 1rem;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  color: rgba(255, 255, 255, 0.9);
  transition: all 0.3s ease;
  border: 1px solid rgba(99, 102, 241, 0.2);
  
  &:focus {
    box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.3);
    border-color: rgba(99, 102, 241, 0.5);
    outline: none;
  }
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
    font-style: italic;
    font-size: 0.9rem;
  }
  
  &:hover:not(:focus) {
    border-color: rgba(99, 102, 241, 0.4);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
  
  &:required {
    border-left: 3px solid rgba(99, 102, 241, 0.7);
  }
  
  &:invalid:not(:placeholder-shown) {
    border-left: 3px solid rgba(239, 68, 68, 0.7);
  }
  
  &:valid:not(:placeholder-shown) {
    border-left: 3px solid rgba(16, 185, 129, 0.7);
  }
`;

const FormSelect = styled.select`
  ${glassMedium}
  padding: 0.75rem 1rem;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  color: rgba(255, 255, 255, 0.9);
  background-color: rgba(15, 23, 42, 0.3);
  appearance: none;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='rgba(255, 255, 255, 0.5)' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 1rem center;
  background-size: 1em;
  border: 1px solid rgba(99, 102, 241, 0.2);
  
  &:focus {
    box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.3);
    border-color: rgba(99, 102, 241, 0.5);
    outline: none;
  }
  
  &:hover:not(:focus) {
    border-color: rgba(99, 102, 241, 0.4);
  }
  
  option {
    background-color: rgba(15, 23, 42, 0.95);
    color: rgba(255, 255, 255, 0.9);
  }
`;

const RequiredFieldsNote = styled.div`
  margin-bottom: 1rem;
  padding: 0.75rem 1rem;
  background: rgba(99, 102, 241, 0.1);
  border-left: 3px solid rgba(99, 102, 241, 0.7);
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.8);
  animation: ${fadeInAnimation} 0.5s ease-out;
  
  span {
    color: rgba(239, 68, 68, 0.8);
    font-weight: 600;
  }
`;

const FormTextarea = styled.textarea`
  ${glassMedium}
  padding: 0.75rem 1rem;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  color: rgba(255, 255, 255, 0.9);
  transition: all 0.3s ease;
  resize: vertical;
  min-height: 100px;
  border: 1px solid rgba(99, 102, 241, 0.2);
  
  &:focus {
    box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.3);
    border-color: rgba(99, 102, 241, 0.5);
    outline: none;
  }
  
  &:hover:not(:focus) {
    border-color: rgba(99, 102, 241, 0.4);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
    font-style: italic;
    font-size: 0.9rem;
  }
  
  &:required {
    border-left: 3px solid rgba(99, 102, 241, 0.7);
  }
`;

const FormActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 1rem;
`;

const CancelButton = styled.button`
  padding: 0.75rem 1.5rem;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: rgba(15, 23, 42, 0.4);
  color: rgba(255, 255, 255, 0.8);
  border: 1px solid rgba(255, 255, 255, 0.1);
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(15, 23, 42, 0.6);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    color: rgba(255, 255, 255, 1);
  }
  
  &:active {
    transform: translateY(-1px);
  }
`;

const SaveButton = styled.button`
  padding: 0.75rem 1.5rem;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: linear-gradient(
    135deg, 
    rgba(79, 70, 229, 0.7) 0%, 
    rgba(99, 102, 241, 0.7) 100%
  );
  color: white;
  border: 1px solid rgba(99, 102, 241, 0.3);
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  position: relative;
  overflow: hidden;
  
  &:hover {
    transform: translateY(-2px);
    background: linear-gradient(
      135deg, 
      rgba(79, 70, 229, 0.8) 0%, 
      rgba(99, 102, 241, 0.8) 100%
    );
    box-shadow: 0 8px 20px rgba(99, 102, 241, 0.2);
  }
  
  &:active {
    transform: translateY(-1px);
  }
  
  &:after {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: linear-gradient(
      to right,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 0.1) 50%,
      rgba(255, 255, 255, 0) 100%
    );
    transform: rotate(30deg);
    transition: all 0.7s ease;
    opacity: 0;
  }
  
  &:hover:after {
    opacity: 1;
    transform: rotate(30deg) translate(100%, -100%);
  }
`;

const ValidationError = styled.div`
  margin-top: 0.75rem;
  padding: 0.75rem 1rem;
  background: rgba(239, 68, 68, 0.1);
  border-left: 3px solid rgba(239, 68, 68, 0.7);
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.9);
  animation: ${fadeInAnimation} 0.3s ease-out;
`;

// Format date for display
const formatDate = (dateString) => {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(date);
};

const WorkshopsList = ({ 
  workshops, 
  onCreateWorkshop, 
  onSelectWorkshop,
  onEditWorkshop, 
  onDeleteWorkshop,
  onAddWorkshop,
  filterText,
  setFilterText,
  filterStatus,
  setFilterStatus
}) => {
  
  // State for modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedWorkshop, setSelectedWorkshop] = useState(null);
  const [isCreateMode, setIsCreateMode] = useState(false);
  const [validationError, setValidationError] = useState('');
  
  // Handle search input change
  const handleSearchChange = (e) => {
    setFilterText(e.target.value);
  };
  
  // Handle status filter change
  const handleStatusFilterChange = (status) => {
    setFilterStatus(status);
  };
  
  // Edit workshop handler
  const handleEditWorkshop = (workshop) => {
    // Call the parent component's function
    onEditWorkshop(workshop);
  };
  
  // Create workshop handler
  const handleCreateWorkshop = () => {
    // Call the parent component's function rather than opening the modal
    onCreateWorkshop();
  };
  
  // Close modal handler
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedWorkshop(null);
    setIsCreateMode(false);
  };
  
  // Save workshop handler (for both create and edit)
  const handleSaveWorkshop = () => {
    // Validate required fields
    setValidationError('');
    
    if (!selectedWorkshop.title || !selectedWorkshop.startDateTime || !selectedWorkshop.location) {
      setValidationError('Please fill in all required fields marked with *');
      return;
    }
    
    if (isCreateMode) {
      // Create a new workshop
      const newWorkshop = {
        ...selectedWorkshop,
        id: `workshop-${Date.now()}`, // Generate a unique ID
        currentParticipants: 0,
        status: selectedWorkshop.status || 'upcoming',
        maxParticipants: selectedWorkshop.maxParticipants || 0
      };
      onAddWorkshop(newWorkshop);
    } else if (selectedWorkshop) {
      // Edit existing workshop
      onEditWorkshop(selectedWorkshop);
    }
    setIsModalOpen(false);
    setSelectedWorkshop(null);
    setValidationError('');
  };
  
  // ESC key to close modal
  useEffect(() => {
    const handleEscKey = (e) => {
      if (e.key === 'Escape') {
        handleCloseModal();
      }
    };
    
    window.addEventListener('keydown', handleEscKey);
    return () => window.removeEventListener('keydown', handleEscKey);
  }, []);
  
  return (
    <WorkshopsContainer>
      <HeaderSection>
        <div></div> {/* Empty div to maintain the flex layout */}
        <CreateButton onClick={handleCreateWorkshop}>
          <PlusIcon>+</PlusIcon>
          Create New Workshop
        </CreateButton>
      </HeaderSection>
      
      <FilterContainer>
        <SearchInput
          type="text"
          placeholder="Search workshops by title or description..."
          value={filterText}
          onChange={handleSearchChange}
        />
        
        <StatusFilter>
          <StatusButton 
            active={filterStatus === 'all'} 
            onClick={() => handleStatusFilterChange('all')}
          >
            All
          </StatusButton>
          <StatusButton 
            active={filterStatus === 'upcoming'} 
            onClick={() => handleStatusFilterChange('upcoming')}
          >
            Upcoming
          </StatusButton>
          <StatusButton 
            active={filterStatus === 'in-progress'} 
            onClick={() => handleStatusFilterChange('in-progress')}
          >
            In Progress
          </StatusButton>
          <StatusButton 
            active={filterStatus === 'completed'} 
            onClick={() => handleStatusFilterChange('completed')}
          >
            Completed
          </StatusButton>
          <StatusButton 
            active={filterStatus === 'cancelled'} 
            onClick={() => handleStatusFilterChange('cancelled')}
          >
            Cancelled
          </StatusButton>
        </StatusFilter>
      </FilterContainer>
      
      {workshops.length > 0 ? (
        <WorkshopsTable>
          <thead>
            <tr>
              <TableHeader>Workshop</TableHeader>
              <TableHeader>Date & Time</TableHeader>
              <TableHeader>Location</TableHeader>
              <TableHeader>Status</TableHeader>
              <TableHeader>Participants</TableHeader>
              <TableHeader>Actions</TableHeader>
            </tr>
          </thead>
          <tbody>
            {workshops.map(workshop => (
              <TableRow key={workshop.id}>
                <TitleCell onClick={() => onSelectWorkshop(workshop)} style={{ cursor: 'pointer' }}>
                  {workshop.title}
                </TitleCell>
                <TableCell>
                  <DateTimeDisplay>
                    <DatePart>{formatDate(workshop.startDateTime).split(',')[0]}</DatePart>
                    <TimePart>{formatDate(workshop.startDateTime).split(',')[1]}</TimePart>
                  </DateTimeDisplay>
                </TableCell>
                <TableCell>
                  <LocationDisplay>
                    <LocationIcon>📍</LocationIcon>
                    <LocationText>{workshop.location}</LocationText>
                  </LocationDisplay>
                </TableCell>
                <TableCell>
                  <StatusBadge status={workshop.status}>
                    {workshop.status === 'upcoming'
                      ? 'Upcoming'
                      : workshop.status === 'in-progress'
                        ? 'In Progress'
                        : workshop.status === 'completed'
                          ? 'Completed'
                          : 'Cancelled'}
                  </StatusBadge>
                </TableCell>
                <TableCell>
                  <ParticipantCount>
                    <CurrentCount>{workshop.currentParticipants || 0}</CurrentCount>
                    <Divider>/</Divider>
                    <MaxCount>{workshop.maxParticipants || '∞'}</MaxCount>
                  </ParticipantCount>
                </TableCell>
                <ActionCell>
                  <ActionButtonsWrapper>
                    <ActionButton variant="secondary" size="small" onClick={() => onSelectWorkshop(workshop)}>
                      View
                    </ActionButton>
                    <ActionButton variant="secondary" size="small" onClick={() => handleEditWorkshop(workshop)}>
                      Edit
                    </ActionButton>
                    <ActionButton variant="danger" size="small" onClick={() => onDeleteWorkshop(workshop.id)}>
                      Delete
                    </ActionButton>
                  </ActionButtonsWrapper>
                </ActionCell>
              </TableRow>
            ))}
          </tbody>
        </WorkshopsTable>
      ) : (
        <NoWorkshopsMessage>
          No workshops found. {filterText || filterStatus !== 'all' ? 'Try adjusting your filters.' : 'Create a new workshop to get started.'}
        </NoWorkshopsMessage>
      )}
      
      {/* Modal functionality is now handled by the parent component through WorkshopForm */}
    </WorkshopsContainer>
  );
};

export default WorkshopsList;